import * as RadixDialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { useI18n } from "@/i18n/provider";
import { cn } from "@/lib/utils";

/**
 * Radix owns focus trapping, the escape key, scroll lock and the aria wiring. We own the
 * paper. A dialog in this product is a form pinned to the desk, not a floating glass card.
 */

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;

  /**
   * `false` removes EVERY casual way out: no ✕, no Escape, no click-outside. The only exit
   * is a control you put in the footer.
   *
   * Reserve it for the one case that earns it: a secret shown once and never again. The
   * plaintext API key exists in the browser for the length of that dialog and nowhere else —
   * the server stored a hash and cannot get it back — so a stray Escape keypress costs the
   * customer a dead sensor gateway and a support ticket. Everywhere else this would be
   * hostile, and Radix's defaults are right.
   *
   * It removes the ✕ as well as disabling it. A rendered control that does nothing is worse
   * than no control: it is a lie the user has to discover by clicking.
   */
  dismissible?: boolean;
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  className,
  dismissible = true,
}: DialogProps) {
  const { t } = useI18n();

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 bg-ink/40 backdrop-blur-[1px]" />
        <RadixDialog.Content
          onEscapeKeyDown={dismissible ? undefined : (e) => e.preventDefault()}
          onPointerDownOutside={dismissible ? undefined : (e) => e.preventDefault()}
          onInteractOutside={dismissible ? undefined : (e) => e.preventDefault()}
          className={cn(
            "fixed start-1/2 top-1/2 z-50 w-[min(32rem,calc(100vw-2rem))]",
            "-translate-y-1/2 translate-x-[var(--dialog-x)] rtl:translate-x-[var(--dialog-x-rtl)]",
            "max-h-[calc(100dvh-4rem)] overflow-y-auto",
            "rounded-[6px] border border-rule-strong bg-paper shadow-[0_16px_48px_rgba(57,54,70,0.18)]",
            className,
          )}
          style={
            {
              // The dialog is centred on the INLINE axis, which flips with direction.
              // A single -50% would push it off-screen in RTL.
              "--dialog-x": "-50%",
              "--dialog-x-rtl": "50%",
            } as React.CSSProperties
          }
        >
          <div className="flex items-start justify-between gap-4 border-b border-rule px-5 py-4">
            <div className="flex flex-col gap-1">
              <RadixDialog.Title className="text-base font-semibold text-text weight-display">
                {title}
              </RadixDialog.Title>
              {description ? (
                <RadixDialog.Description className="text-sm text-muted">
                  {description}
                </RadixDialog.Description>
              ) : (
                // Radix warns without one, and a dialog with no description is still
                // describable — by its title.
                <RadixDialog.Description className="sr-only">{title}</RadixDialog.Description>
              )}
            </div>
            {/* No ✕ when the dialog is not dismissible. Not a disabled ✕ — no ✕ at all.
                A rendered control that does nothing is a lie the user has to discover by
                clicking it. */}
            {dismissible ? (
              <RadixDialog.Close
                className="rounded-[4px] p-1 text-faint transition-colors hover:bg-paper-sunk hover:text-text focus-visible:outline-2 focus-visible:outline-cream"
                aria-label={t("common.close")}
              >
                <X className="size-4" aria-hidden />
              </RadixDialog.Close>
            ) : null}
          </div>

          <div className="px-5 py-4">{children}</div>

          {footer ? (
            <div className="flex items-center justify-end gap-2 border-t border-rule bg-paper-sunk px-5 py-3">
              {footer}
            </div>
          ) : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

export const DialogClose = RadixDialog.Close;
